import { DateTime } from "luxon";
import type { PbjCategoryScore, PbjTrackedPlan, PbjTrackedPlanAction, PbjTrackedPlanSaveBody } from "../../../utils/models";
import { currentFyQuarter, fyQuarterRange } from "../fyQuarter";
import { computeCumulativePass } from "./cumulativeScoring.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import { type PlanItem, RECOMMENDATION_TYPES } from "./recommendations.constants";

/**
 * Two spends are "the same" below half a cent. The on-screen figures come from slider drags and
 * BE-suggested steps that round differently, so an exact float compare would report a plan as
 * changed the moment it round-tripped through the API.
 */
const SPEND_EPSILON = 0.005;

/**
 * The FULL federal-fiscal quarter containing `date`.
 *
 * The tracked plan is anchored to a quarter, NOT to the Prediction tab's committed range — which
 * defaults to quarter-start-through-today. Saving that range verbatim would commit the department
 * to a window that ends today, and every goal would then be spread over the days already elapsed
 * instead of the ones still to come.
 */
export const quarterWindowFor = (isoDate: string): { start: string; end: string } => {
	const { year, quarter } = currentFyQuarter(DateTime.fromISO(isoDate));
	const { start, end } = fyQuarterRange(year, quarter);
	return { start: start.toISODate() ?? isoDate, end: end.toISODate() ?? isoDate };
};

/**
 * Turn the on-screen Action Plan into the `actions[]` the tracked-plan API stores.
 *
 * `gains` must cover EXACTLY the measures each lever moves or the API answers 422, so the entries
 * are enumerated from `RECOMMENDATION_TYPES[].impactedIds` (the FE mirror of the BE's
 * `PBJ_TRACKED_PLAN_ACTION_CATEGORIES`) rather than from whatever the plan item happens to carry.
 *
 * Points come from the cumulative pass, not from each item's standalone `points`: the stored gain
 * is the marginal one the Action Plan credited this action over the items added BEFORE it, which
 * is what the tracker's per-action points must reconcile against.
 */
export const buildTrackedPlanActions = (selectedPlan: PlanItem[], categories: PbjCategoryScore[]): PbjTrackedPlanAction[] => {
	const cumulative = computeCumulativePass(selectedPlan, categories);
	return selectedPlan.flatMap((item): PbjTrackedPlanAction[] => {
		const rec = RECOMMENDATION_TYPES.find((r) => r.id === item.id);
		if (!rec) return [];
		const gapByCategory = new Map((item.breakdown ?? []).map((b) => [b.categoryId, b.hppdGap]));
		const pointsByCategory = cumulative.byItemId[item.id]?.pointsByCategory ?? {};
		return [
			{
				id: rec.id,
				hours: item.hours,
				hoursCatchup: item.hoursCatchup,
				spend: item.spend,
				gains: rec.impactedIds.map((categoryId) => ({
					categoryId,
					// DEFAULT: a measure the lever moves but that this item has no breakdown entry for
					// records a zero gain rather than being omitted. An omission is a 422 — the API
					// requires every moved measure — and the honest reading of "the scorer never
					// produced a gap for this category" (an N/A measure absent from
					// `prediction.categories`) is that the action gains nothing there.
					hppdGap: gapByCategory.get(categoryId) ?? 0,
					points: pointsByCategory[categoryId] ?? 0,
				})),
			},
		];
	});
};

/** Assemble the full POST body for the currently assembled plan. */
export const buildTrackedPlanSaveBody = (args: {
	departmentId: string;
	selectedPlan: PlanItem[];
	categories: PbjCategoryScore[];
	periodMode: PeriodMode;
	// Any date inside the quarter being planned — the page's committed start.
	committedStart: string;
	targetPoints: number;
}): PbjTrackedPlanSaveBody => {
	const { start, end } = quarterWindowFor(args.committedStart);
	return {
		departmentId: args.departmentId,
		periodMode: args.periodMode,
		quarterStart: start,
		quarterEnd: end,
		targetPoints: args.targetPoints,
		actions: buildTrackedPlanActions(args.selectedPlan, args.categories),
	};
};

/**
 * Whether the on-screen plan is the one already being tracked.
 *
 * Compares the action set and each action's spend (with a cent-scale epsilon), plus the period
 * mode and quarter window. The window and mode are in the comparison because they change WHAT is
 * measured, not just how much: a plan re-saved as "quarter" commits to the sustained rate where
 * "qtd" committed to the catch-up rate, so an identical action set under a different mode is a
 * genuinely different commitment and must offer "Switch tracker", not read as already tracked.
 */
export const trackedPlanMatches = (tracked: PbjTrackedPlan | null, next: PbjTrackedPlanSaveBody): boolean => {
	if (!tracked) return false;
	if (tracked.periodMode !== next.periodMode) return false;
	// The saved plan's dates arrive as Luxon `DateTime`s (the axios interceptor parses every ISO
	// date in a response), while `next` holds the ISO strings we are about to POST. Comparing the
	// two directly is never equal, which silently pins the button to "Switch tracker" forever.
	if (tracked.quarterStart.toISODate() !== next.quarterStart || tracked.quarterEnd.toISODate() !== next.quarterEnd) return false;
	if (tracked.actions.length !== next.actions.length) return false;
	const trackedById = new Map(tracked.actions.map((a) => [a.id, a]));
	return next.actions.every((action) => {
		const saved = trackedById.get(action.id);
		return saved !== undefined && Math.abs(saved.spend - action.spend) < SPEND_EPSILON;
	});
};

/** The three states the "Add to tracker" button can be in. */
export type TrackerButtonState = "untracked" | "tracked" | "switch";

export const trackerButtonState = (tracked: PbjTrackedPlan | null, next: PbjTrackedPlanSaveBody): TrackerButtonState => {
	if (!tracked) return "untracked";
	return trackedPlanMatches(tracked, next) ? "tracked" : "switch";
};
